import { Injectable, OnDestroy } from "@angular/core";
import { Sort } from "@angular/material/sort";
import { Params, Router } from "@angular/router";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import {
	canViewDashboard,
	hasPermissionByKey,
	hasUserRoleControlling,
	hasUserRoleManager,
	isSupervisor,
	useAdmin,
	usePermission,
} from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import { useRouteMatcher } from "@app/_helpers/routerUtils";
import { createRxValueWithCache } from "@app/_helpers/rx-value";
import { createRxValue, distinctUntilChangedJson, fromRxValue, hasPermission } from "@app/_helpers/utils";
import { AppService } from "@app/app.service";
import {
	AuditFilterData,
	AuditFilterDefaultData,
} from "@app/components/audit-filter-popover/audit-filter-popover.component";
import RoundingConfigData from "@app/shared/dialogs/rounding-dialog/models/rounding-config-data";
import { RoundingTypes } from "@app/shared/dialogs/rounding-dialog/rounding-dialog.component";
import { ItemBillingFlag } from "@app/shared/filters/item-billing-flag";
import { ItemCompletedFlag } from "@app/shared/filters/item-completed-flag";
import { DashboardConfig, DashboardTabTypes } from "@app/shared/save-dashboard-dialog/dashboardConfig";
import {
	parseRangeFromQuery,
	rangeQueryParamsEqual,
	buildRangeQueryParams as toRangeQueryParams,
} from "@app/shared/time-range-picker/range-query-params";
import {
	getTimeRangeByKey,
	TIME_RANGE_CUSTOM_NAME,
	TIME_RANGE_DEFAULT_RANGES,
	TRange,
} from "@app/shared/time-range-picker/time-range-constants";
import { environment } from "@env/environment";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	ApplicationSettingsQuery,
	ComegoTime,
	Logger,
	ProjectsQuery,
	Tag,
	TagsQuery,
	TagType,
	Task,
	Time,
	TimesService,
	UserSettings,
	UserSettingsQuery,
} from "@timeghost/web-api";
import { RoundingData } from "@timeghost/web-api/lib/services/times.service";
import {
	addDays,
	addHours,
	endOfWeek,
	isValid,
	isWithinInterval,
	roundToNearestMinutes,
	startOfDay,
	startOfWeek,
	subHours,
} from "date-fns/esm";
import { isEqual } from "lodash-es";
import { combineLatest, distinctUntilChanged, filter, map, Subject, shareReplay, startWith, switchMap } from "rxjs";
import { GroupType } from "./data-table/data-table.component";
import {
	filterProjectHoursForReports,
	sumProjectHourDurationStats,
	uniqueUserIdsFromReportItems,
	withProjectHourEditableFlags,
} from "./reports-filter-project-hours";
import { computeReportsScheduleStats } from "./reports-schedule-stats";
import {
	buildReportsShareQueryParams,
	buildShareUrlFromParams,
	hasReportsShareFilterParams,
	planReportsDeepLinkConsume,
	REPORTS_SHARE_FILTER_KEYS,
} from "./share-query-params";
import {
	attachLegacyProjectHourViolations,
	ReportItem,
	reportsRoundingHandler,
	useTransformTimeToReportItem,
} from "./utils";

export {
	hasReportsShareFilterParams,
	REPORTS_SHARE_FILTER_KEYS,
	REPORTS_SHARE_FILTER_PARAM,
} from "./share-query-params";

const log = new Logger("ReportsService");

export type ReportsTab = "summary" | "detailed" | "schedules" | "contingent";
export type ReportsPermissionKind = "project" | "workinghours";

const TAB_TO_PAGE: Record<ReportsTab, string> = {
	summary: "project.simple",
	detailed: "project.detailed",
	schedules: "workinghours",
	contingent: "contingent",
};

const SHOW_FILTERS_BY_TAB: Record<ReportsTab, string[] | null> = {
	summary: null,
	detailed: null,
	schedules: ["team", "group", "comegoType", "audit"],
	contingent: ["team", "group"],
};

export function permissionKindFromTab(tab: ReportsTab | null): ReportsPermissionKind {
	return tab === "schedules" || tab === "contingent" ? "workinghours" : "project";
}

export function canManageSchedulesOthers(user: UserSettings): boolean {
	const isUserAdmin = !!user.workspace.users?.find((x) => x.id === user.id && x.admin);
	const isWorkspaceManager = hasUserRoleManager(user);
	const isControlling = hasUserRoleControlling(user);
	const isUserSupervisor = isSupervisor(user);
	return isUserAdmin || isUserSupervisor || isWorkspaceManager || isControlling;
}

export type ExportFilters = {
	startDate: Date;
	endDate: Date;
	cutOffDate: Date;
	clients: string[];
	projects: string[];
	tags: string[];
	projectTags: string[];
	users: string[];
	groups: string[];
	tasks: string[];
	comegoType: string[];
	rounding: RoundingData;
	billable: string;
	projectStatus: string;
};

export type ContingentFilterState = {
	maxCutOffDate: Date | null;
};
type SavedDashboard = DashboardConfig & {
	editing: boolean;
	workspaceId: string;
	user: { id: string; name: string };
	paritionKey: string;
	cosmosEntityName: string;
};
export type ReportsReloadOptions = {
	silent?: boolean;
};

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class ReportsService implements OnDestroy {
	private _timeLoading = createRxValue(true);
	readonly timeLoading$ = this._timeLoading.asObservable().pipe(distinctUntilChanged());
	get timeLoading() {
		return this._timeLoading.value;
	}
	set timeLoading(val: boolean) {
		this._timeLoading.next(val);
	}
	readonly workinghours = createRxValue<ReportItem<ComegoTime | Time>[]>([]);
	readonly projecthours = createRxValue<ReportItem<Time>[]>([]);
	readonly data$ = combineLatest([this.workinghours.asObservable(), this.projecthours.asObservable()]).pipe(
		map(([workinghours, projecthours]) => ({ comego: workinghours, times: projecthours }))
	);
	readonly roundingData$ = this.appService.roundingData$;
	readonly detailedSort = createRxValueWithCache<Sort>(
		"reports.sort",
		(this.appSettings.getValue().reports?.detailedSort as unknown as Sort) ?? {
			active: "time",
			direction: "desc",
		}
	);
	readonly groupBy = createRxValue<GroupType>(GroupType.Billable);
	readonly currentPage = createRxValue<string>(null);
	readonly reportsTab = useRouteMatcher({
		summary: /^\/dashboard\/?$/,
		detailed: /^\/dashboard\/detailed$/,
		schedules: /^\/dashboard\/schedules$/,
		contingent: /^\/dashboard\/contingent$/,
	});
	readonly lastContingentUrl = createRxValue<string>(null);

	private _reload = new Subject<ReportsReloadOptions>();
	private readonly reloadTrigger$ = this._reload.asObservable().pipe(
		startWith({} as ReportsReloadOptions),
		map((options) => ({ _reloadstamp: Date.now(), silent: !!options?.silent }))
	);
	readonly range = createRxValue(TIME_RANGE_DEFAULT_RANGES.week);

	readonly range$ = this.range.asObservable().pipe(debounceTimeAfterFirst(100), distinctUntilChangedJson());
	private pipelinesStarted = false;
	private workinghoursFetchGen = 0;
	private projecthoursFetchGen = 0;
	readonly showFilters = createRxValue<string[] | null>(null);
	readonly showFilters$ = this.showFilters.asObservable().pipe(
		map((x) => {
			const enabledKeys =
				!x || typeof x !== "object"
					? ["team", "group", "task", "client", "project", "tag", "billable", "audit", "amount"]
					: x;
			return enabledKeys.reduce(
				(acc, r) => {
					acc[r] = true;
					return acc;
				},
				{ enabled: !!enabledKeys?.length }
			);
		})
	);
	constructor(
		private timesService: TimesService,
		private projectsQuery: ProjectsQuery,
		private userSettingsQuery: UserSettingsQuery,
		private appService: AppService,
		private appSettings: ApplicationSettingsQuery,
		private router: Router,
		private tagsQuery: TagsQuery
	) {
		combineLatest([this.reportsTab.asObservable(), this.userSettingsQuery.select()])
			.pipe(untilDestroyed(this))
			.subscribe(([tab]) => this.syncTabState(tab as ReportsTab | null));
		this.syncTabState(this.reportsTab.value as ReportsTab | null);
	}
	private syncTabState(tab: ReportsTab | null) {
		if (!tab) return;
		const page = TAB_TO_PAGE[tab];
		if (this.currentPage.value !== page) this.currentPage.update(page);
		const user = this.userSettingsQuery.getValue();
		let filters = SHOW_FILTERS_BY_TAB[tab];
		if (tab === "schedules" && !canManageSchedulesOthers(user)) {
			filters = ["audit"];
		}
		if (JSON.stringify(this.showFilters.value) !== JSON.stringify(filters)) {
			this.showFilters.update(filters);
		}
		// Contingent has its own fetch/loading; shared pipelines do not run here.
		if (tab === "contingent" && this.timeLoading) {
			this.timeLoading = false;
		}
	}
	private get http() {
		return this.timesService.adapter; // adapter is base service for any entity
	}
	readonly userIsAdmin = useAdmin();
	readonly userIsWorkspaceManager = this.userSettingsQuery.select().pipe(map((x) => hasUserRoleManager(x)));
	readonly amountToggle = createRxValue(!!this.appSettings.getValue().reports?.amount);
	readonly noStartDateToggle = createRxValue(!!this.appSettings.getValue().reports?.noStartDateToggle);
	readonly canViewAmount = this.userSettingsQuery
		.select()
		.pipe(
			map(
				(x) =>
					x.workspace.permissionSettings.groupsCanSeeBillableRates.findIndex(
						(p) => !!hasPermission(p.id, x)
					) !== -1
			)
		);
	readonly showAmounts$ = combineLatest([this.amountToggle.asObservable(), this.canViewAmount]).pipe(
		map(([amount, canView]) => amount && canView)
	);
	readonly workinghoursEnabled$ = this.appService.comegoEnabled$;
	readonly projecthoursEnabled$ = this.appService.projecthoursEnabled$;
	readonly scheduleEnabled$ = this.userSettingsQuery
		.select()
		.pipe(map((x) => !!x?.workspace?.schedules?.workspace?.enabled));
	readonly comegoExportVisible$ = this.userSettingsQuery.select().pipe(
		map((user) => {
			return hasPermissionByKey(user, "groupsCanComegoExportTimes");
		})
	);
	readonly permissions$ = usePermission();
	readonly contingentVisible$ = this.userSettingsQuery.select().pipe(
		map((user) => {
			if (!user.workspace?.["timecontingent"]?.["enabled"]) return false;
			return (
				hasPermissionByKey(user, "groupsCanContingentViewSelf") ||
				(user.workspace["timecontingent"]?.["superviseCanViewSubordinates"] && isSupervisor(user))
			);
		})
	);
	readonly viewOnlyOwnContingent = combineLatest([
		this.userSettingsQuery.select().pipe(
			map((user) => {
				if (this.userIsAdmin.value || hasUserRoleManager(user))
					return user.workspace.users
						.filter((d) => !d.removed)
						.map((u) => ({ ...u, isSupervisor: !!u["supervisors"]?.find((s: any) => s.id === user.id) }));
				const subordinates = user.workspace.users
					?.filter((u) => !u.removed && u["supervisors"]?.find((s: any) => s.id === user.id))
					.map((u) => ({ ...u, isSupervisor: true }));
				return subordinates;
			})
		),
	]).pipe(
		map(([subordinates]) => {
			const admin = this.userIsAdmin.value;
			return !(admin || (subordinates && subordinates.length));
		})
	);
	readonly legacyDate$ = fromRxValue(
		this.userSettingsQuery.select().pipe(
			untilDestroyed(this),
			map((user) => new Date(user.workspace.settings["legacyTimesBefore"]))
		)
	);
	readonly visibleNav$ = combineLatest([this.userSettingsQuery.select(), this.contingentVisible$]).pipe(
		map(([user, contingent]) => {
			const permission = canViewDashboard(user);
			if (!permission) return null;
			const phEnabled = user?.workspace?.settings?.projecthoursEnabled;
			const ccEnabled = !!user?.workspace?.settings?.workinghoursEnabled;
			return {
				project: phEnabled,
				workinghoursOnly: !phEnabled,
				workinghours: ccEnabled,
				workinghoursExport: hasPermissionByKey(user, "groupsCanComegoExportTimes"),
				contingent,
				showDivider: permission.workingHoursPermission && permission.projectPermission,
				permission,
			};
		})
	);
	readonly groupFilter = createRxValueWithCache<string[]>("reports.filter.groups", []);
	readonly taskFilter = createRxValueWithCache<string[]>("reports.filter.tasks", []);
	readonly clientFilter = createRxValueWithCache<string[]>("reports.filter.clients", []);
	readonly comegoTypeFilter = createRxValueWithCache<string[]>("reports.filter.comegoType", []);
	readonly tagFilter = createRxValueWithCache<Tag[]>("reports.filter.tags", []);
	readonly projectFilter = createRxValueWithCache<string[]>("reports.filter.projects", []);
	readonly auditFilter = createRxValueWithCache<AuditFilterData>("reports.filter.audit", {
		...AuditFilterDefaultData,
	});

	readonly auditFilter$active = combineLatest([this.auditFilter.asObservable(), this.reportsTab.asObservable()]).pipe(
		map(([x, tab]) => x && (x.project || x.task || x.duration || (tab === "schedules" && x.rulebreaks)))
	);
	readonly teamFilter = createRxValueWithCache<string[]>("reports.filter.team", [
		this.userSettingsQuery.getValue().id,
	]);
	readonly billableFilter = createRxValueWithCache<ItemBillingFlag>("reports.filter.billable", ItemBillingFlag.BOTH);
	readonly billableFilter$active = this.billableFilter.asObservable().pipe(map((x) => x !== ItemBillingFlag.BOTH));
	readonly completedFilter = createRxValueWithCache<ItemCompletedFlag>(
		"reports.filter.completed",
		ItemCompletedFlag.NOT_COMPLETED
	);
	readonly completedFilter$active = this.completedFilter
		.asObservable()
		.pipe(map((x) => x !== ItemCompletedFlag.NOT_COMPLETED));
	readonly showCompletedProjects$ = this.completedFilter$active;
	readonly lastDashboard = createRxValueWithCache<string>(
		"reports.lastDashboard",
		((this.appSettings.getValue().reports?.lastDashboard as any) || null) as string | null
	);
	readonly dashboards = createRxValue<SavedDashboard[]>([]);
	readonly tasks = createRxValue<any[]>([]);
	readonly activeFavDashboard = createRxValue<SavedDashboard>(null);
	readonly employeeFilterIncludesRemoved$ = createRxValue<boolean>(false);
	readonly untilDate = createRxValue<Date>(new Date());
	readonly contingentSort = createRxValue<Sort>({ active: "contingent", direction: "asc" });
	readonly contingentFilterState = createRxValue<ContingentFilterState>({
		maxCutOffDate: null,
	});
	readonly theme = this.userSettingsQuery.select().pipe(map((x) => x.settings.theme));
	readonly hasActiveFilter = combineLatest([
		this.teamFilter.asObservable(),
		this.groupFilter.asObservable(),
		this.taskFilter.asObservable(),
		this.clientFilter.asObservable(),
		this.tagFilter.asObservable(),
		this.projectFilter.asObservable(),
		this.comegoTypeFilter.asObservable(),
	]).pipe(
		map((args: any[][]) => {
			return args.findIndex((x) => !!x?.length) !== -1;
		})
	);
	readonly pinnedIds$ = this.userSettingsQuery
		.select()
		.pipe(
			map((user: any) =>
				(user.pinnedDashboards ?? [])
					.filter(
						(dashboard: any) => dashboard?.workspaceId === this.userSettingsQuery.getValue().workspace.id
					)
					.map((dashboard: any) => dashboard.id as string)
			)
		);
	readonly dashboardsPinnedByUser$ = combineLatest([
		this.dashboards.asObservable(),
		this.pinnedIds$,
		this.currentPage.asObservable(),
	]).pipe(
		map(([dashboards, pinnedIds, route]: [SavedDashboard[], string[], string]) => {
			const tabTypeValue =
				route === "contingent"
					? DashboardTabTypes.CONTINGENT
					: route === "workinghours"
						? DashboardTabTypes.WORKING_HOURS
						: DashboardTabTypes.PROJECT_HOURS;
			return dashboards
				.filter((x: SavedDashboard) => x.tabType === tabTypeValue)
				.filter((x) => pinnedIds.includes(x.id))
				.sort((a, b) => {
					const indexA = pinnedIds.findIndex((ref: string) => ref === a.id);
					const indexB = pinnedIds.findIndex((ref: string) => ref === b.id);
					return indexA - indexB;
				});
		})
	);
	readonly showManageDashboards$ = this.dashboardsPinnedByUser$.pipe(
		map((dashboards) => (dashboards?.length ?? 0) === 0)
	);
	readonly isDefaultFilter$ = combineLatest([
		this.teamFilter.asObservable(),
		this.groupFilter.asObservable(),
		this.projectFilter.asObservable(),
		this.clientFilter.asObservable(),
		this.taskFilter.asObservable(),
		this.tagFilter.asObservable(),
		this.range$,
		this.auditFilter.asObservable(),
		this.billableFilter.asObservable(),
		this.completedFilter.asObservable(),
		this.comegoTypeFilter.asObservable(),
		this.currentPage.asObservable(),
	]).pipe(
		map(() => {
			const defaultDashboard = {
				team: [this.userSettingsQuery.getValue().id],
				group: [] as string[],
				project: [] as string[],
				client: [] as string[],
				task: [] as string[],
				tag: [] as unknown[],
				audit: AuditFilterDefaultData,
				billable: ItemBillingFlag.BOTH,
				completed: ItemCompletedFlag.NOT_COMPLETED,
				comegoType: [] as string[],
				rangeType: "time-range.preset.week",
				start: startOfDay(startOfWeek(new Date(), { weekStartsOn: 1 })),
				end: startOfDay(endOfWeek(new Date(), { weekStartsOn: 1 })),
			};
			const filters = {
				team: this.teamFilter.value,
				group: this.groupFilter.value,
				project: this.projectFilter.value,
				client: this.clientFilter.value,
				task: this.taskFilter.value,
				tag: this.tagFilter.value,
				audit: this.auditFilter.value,
				billable: this.billableFilter.value,
				completed: this.completedFilter.value,
				comegoType: this.comegoTypeFilter.value,
				rangeType: this.range.value.name ?? "time-range.preset.custom",
				start: startOfDay(this.range.value.from),
				end: startOfDay(this.range.value.to),
			};
			return isEqual(filters, defaultDashboard);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	readonly users$ = combineLatest([
		this.teamFilter.asObservable(),
		this.groupFilter.asObservable(),
		this.userSettingsQuery.select(),
	]).pipe(
		map(([users, groups, user]) => {
			return groups.reduce((acc, r) => {
				const grp = user.workspace.groups.find((x) => x.id === r);
				acc.push(
					...grp.users
						.map((x) => x.id)
						.filter(
							(u) => !acc.find((au) => au === u) && user.workspace.users.find((wuser) => wuser.id === u)
						)
				);
				return acc;
			}, users || [user.id]);
		})
	);
	readonly data$filtered = fromRxValue(
		combineLatest([
			this.projecthours.asObservable(),
			this.billableFilter.asObservable(),
			this.auditFilter.asObservable(),
			this.userSettingsQuery.select(),
			this.showFilters$,
		]).pipe(
			untilDestroyed(this),
			switchMap(
				async ([data, billable, audit, , show]: [
					ReportItem<Time>[],
					ItemBillingFlag,
					AuditFilterData,
					UserSettings,
					{ [key: string]: boolean },
				]) => {
					return filterProjectHoursForReports(data, billable, audit, show, {
						viewerTimezone: this.userSettingsQuery.getValue()?.settings?.timeZone,
					});
				}
			),
			map((x) => withProjectHourEditableFlags(x))
		)
	);
	readonly stats$ = combineLatest([
		this.data$filtered.asObservable(),
		this.userSettingsQuery.select(),
		this.range$,
	]).pipe(
		map(([times, user, range]) =>
			computeReportsScheduleStats({
				times,
				user,
				from: range.from,
				to: range.to,
			})
		)
	);
	readonly data$stats = combineLatest([
		this.data$filtered.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: true })),
		this.roundingData$,
	]).pipe(
		reportsRoundingHandler<ReportItem<Time>>(),
		map(([x]) => sumProjectHourDurationStats(x)),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	roundingHandler<T extends ReportItem>(overrideEnabled?: boolean) {
		return reportsRoundingHandler<T>(overrideEnabled);
	}
	readonly usersInDataset = fromRxValue(
		this.data$.pipe(
			untilDestroyed(this),
			map(({ comego, times }) => uniqueUserIdsFromReportItems([...comego, ...times]))
		)
	);
	reload(options?: ReportsReloadOptions) {
		this._reload.next(options ?? {});
	}
	private hasSameRangeState(range: TRange): boolean {
		if (!range?.from || !range?.to) return false;
		const currentRange = this.range.value;
		return (
			currentRange?.from?.getTime?.() === new Date(range.from).getTime() &&
			currentRange?.to?.getTime?.() === new Date(range.to).getTime() &&
			(currentRange?.name || TIME_RANGE_CUSTOM_NAME) === (range?.name || TIME_RANGE_CUSTOM_NAME)
		);
	}
	buildRangeQueryParams(range: TRange = this.range.value): Params {
		return toRangeQueryParams(range);
	}
	/** Shareable query params: range + non-default toggles only. */
	buildShareQueryParams(range: TRange = this.range.value): Params {
		return buildReportsShareQueryParams({
			range,
			billable: this.billableFilter.value,
			completed: this.completedFilter.value,
			amount: this.amountToggle.value,
			noStartDate: this.noStartDateToggle.value,
		});
	}
	buildShareUrl(): string {
		const path = this.router.url.split("?")[0] || "/dashboard";
		return buildShareUrlFromParams(path, this.buildShareQueryParams());
	}
	syncRangeToUrl(range: TRange = this.range.value) {
		const nextQueryParams: Params = {
			...this.buildRangeQueryParams(range),
			...REPORTS_SHARE_FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: null }), {} as Params),
		};
		const currentQueryParams = this.router.parseUrl(this.router.url).queryParams;
		if (rangeQueryParamsEqual(currentQueryParams, nextQueryParams)) return;
		this.router.navigate([], {
			queryParams: nextQueryParams,
			queryParamsHandling: "merge",
		});
	}
	onRangeUpdate(range: TRange): boolean {
		if (!this.updateRange(range)) return false;
		this.syncRangeToUrl(this.range.value);
		return true;
	}
	updateRange(range: TRange): boolean {
		const from = new Date(range?.from);
		const to = new Date(range?.to);
		if (!isValid(from) || !isValid(to)) return false;
		const nextRange = {
			...range,
			from,
			to,
		} as TRange;
		if (this.hasSameRangeState(nextRange)) return false;
		this.range.update(nextRange);
		return true;
	}
	updateRangeFromQuery(queryParams: Params): boolean {
		const updatedRange = parseRangeFromQuery(queryParams);
		if (!updatedRange) return false;
		if (this.hasSameRangeState(updatedRange)) return false;
		this.range.update(updatedRange);
		return true;
	}
	/** Share/deep-link query keys already applied within the current navigation. */
	private consumedShareFilterKeys = new Set<string>();
	private consumedNavigationId: number | null = null;

	/** Consumed keys are scoped to one navigation, so a new deep link always re-applies. */
	private resetConsumedKeysForNavigation() {
		const navigationId = this.router.getCurrentNavigation()?.id ?? null;
		if (navigationId !== null && navigationId === this.consumedNavigationId) return;
		this.consumedNavigationId = navigationId;
		this.consumedShareFilterKeys.clear();
	}

	applyDeepLinkQuery(queryParams: Params) {
		this.consumeDeepLinkQuery(queryParams);
	}
	/**
	 * Apply share toggles + deep-link user/project once, then strip those keys.
	 * Range-only query updates must not change team/group/project or other filters.
	 */
	consumeDeepLinkQuery(queryParams: Params): { applied: boolean; stripKeys: string[] } {
		if (!hasReportsShareFilterParams(queryParams)) {
			return { applied: false, stripKeys: [] };
		}
		this.resetConsumedKeysForNavigation();

		const user = this.userSettingsQuery.getValue();
		const workspaceUserIds = new Set((user.workspace.users ?? []).map((workspaceUser) => workspaceUser.id));
		let projectUserIds: string[] = [];
		if (queryParams?.user === "all" && queryParams?.project) {
			projectUserIds = [...workspaceUserIds];
		}

		const plan = planReportsDeepLinkConsume({
			queryParams,
			workspaceUserIds,
			projectUserIds,
			consumedKeys: this.consumedShareFilterKeys,
		});

		for (const key of plan.consumedKeys) {
			this.consumedShareFilterKeys.add(key);
		}

		if (plan.project) this.projectFilter.update(plan.project);
		if (plan.team) this.teamFilter.update(plan.team);
		if (plan.clearGroups) this.groupFilter.update([]);
		if (plan.clearTasks) this.taskFilter.update([]);
		if (plan.billable !== undefined) this.billableFilter.update(plan.billable);
		if (plan.completed !== undefined) this.completedFilter.update(plan.completed);
		if (plan.amount !== undefined) this.amountToggle.update(plan.amount);
		if (plan.noStartDate !== undefined) this.noStartDateToggle.update(plan.noStartDate);

		return { applied: plan.stripKeys.length > 0, stripKeys: plan.stripKeys };
	}
	resolveTeamIds(teamIds: string[] = this.teamFilter.value, groupIds: string[] = this.groupFilter.value): string[] {
		const user = this.userSettingsQuery.getValue();
		const teamFromGroups: string[] = groupIds
			.map((groupId) => user.workspace.groups.find((group) => group.id === groupId))
			.filter((group) => !!group)
			.reduce((list, group) => [...list, ...(group.users?.map((workspaceUser) => workspaceUser.id) ?? [])], [])
			.filter((workspaceUserId) => !!workspaceUserId)
			.uniq();
		return [...(teamIds || []), ...teamFromGroups].uniq();
	}
	getTagFilters(tagFilter: Tag[] = this.tagFilter.value) {
		return tagFilter.reduce(
			(tagCollection, tag) => {
				if (tag.tagType === TagType.Project) tagCollection.project.push(tag.id);
				else if (tag.tagType === TagType.Time) tagCollection.times.push(tag.id);
				return tagCollection;
			},
			{ project: [], times: [] } as { project: string[]; times: string[] }
		);
	}
	getRoundingOptions(): RoundingData {
		return this.appService.roundingData?.enabled === true
			? {
					enabled: true,
					minutes: this.appService.roundingData.minutes,
					type:
						this.appService.roundingData.type === RoundingTypes.UpTo
							? "upto"
							: this.appService.roundingData.type === RoundingTypes.Nearest
								? "nearest"
								: this.appService.roundingData.type === RoundingTypes.DownTo
									? "downto"
									: null,
				}
			: ({
					enabled: false,
				} as any);
	}
	testProjectTimeFilter(time: Time): boolean {
		const team = this.resolveTeamIds();
		const tags = this.getTagFilters();
		if (team.length > 0 && !team.find((x) => x === time.user?.id)) return false;
		if (this.clientFilter.value.length > 0 && !this.clientFilter.value.find((x) => x === time.client?.id))
			return false;
		if (this.projectFilter.value.length > 0 && !this.projectFilter.value.find((x) => x === time.project?.id))
			return false;
		if (this.taskFilter.value.length > 0 && !this.taskFilter.value.find((x) => x === time.task?.id)) return false;
		if (tags.times.length > 0 && !tags.times.find((tagId) => !!time.tags?.find((tag) => tag.id === tagId)))
			return false;
		if (
			tags.project.length > 0 &&
			time.project?.tags &&
			!tags.project.find((tagId) => !!time.project?.tags?.find((tag) => tag.id === tagId))
		)
			return false;
		return true;
	}
	getExportFilters(rounding: RoundingData = this.getRoundingOptions()): ExportFilters {
		const tags = this.getTagFilters();
		let filters = {
			startDate: this.range.value?.from,
			endDate: this.range.value?.to,
			cutOffDate: this.untilDate.value,
			clients: this.clientFilter.value.length > 0 ? this.clientFilter.value : undefined,
			projects: this.projectFilter.value.length > 0 ? this.projectFilter.value : undefined,
			tags: tags.times.length > 0 ? tags.times : undefined,
			projectTags: tags.project.length > 0 ? tags.project : undefined,
			users: this.teamFilter.value.length > 0 ? this.teamFilter.value : undefined,
			groups: this.groupFilter.value.length > 0 ? this.groupFilter.value : undefined,
			tasks: this.taskFilter.value.length > 0 ? this.taskFilter.value : undefined,
			comegoType: this.comegoTypeFilter.value.length > 0 ? this.comegoTypeFilter.value : undefined,
			rounding,
			billable:
				this.billableFilter.value === ItemBillingFlag.BILLED
					? "billable"
					: this.billableFilter.value === ItemBillingFlag.NOT_BILLED
						? "not_billable"
						: this.billableFilter.value === ItemBillingFlag.BOTH
							? "both"
							: undefined,
			projectStatus: this.completedFilter.value === ItemCompletedFlag.NOT_COMPLETED ? "not_completed" : "both",
		};
		if (filters.groups?.length > 0) {
			const user = this.userSettingsQuery.getValue();
			filters.users = [
				...(filters.users || []),
				...Object.keys(
					filters.groups.reduce(
						(users, groupId) => {
							const group = user.workspace.groups.find((x) => x.id === groupId);
							if (!group) return users;
							group.users
								.filter((workspaceUser) => !(workspaceUser.id in users))
								.forEach((workspaceUser) => (users[workspaceUser.id] = null));
							return users;
						},
						{} as { [key: string]: any }
					)
				),
			].uniq();
		}
		if (filters.comegoType && filters.comegoType.includes("work")) {
			filters.comegoType = [...filters.comegoType, "pause"];
		}
		return filters;
	}
	getComegoTimesFiltered(
		from: Date,
		to: Date,
		users: string[] | string,
		options: Partial<{ itemType: string[] | null; rulebreaks: boolean }> = { itemType: null, rulebreaks: false }
	) {
		return this.http
			.post(environment.serverUrl + "/reports", {
				users,
				itemType: options.itemType?.length ? options.itemType : undefined,
				rulebreaks: !!options.rulebreaks,
				range: {
					start: subHours(from, 12).toISOString(),
					end: addHours(to, 12).toISOString(),
				},
				type: "workinghours",
			})
			.toPromise<{ data: ReportItem<ComegoTime>[] }>()
			.then((times) =>
				times?.data?.map((t) => {
					const start = new Date(t.start);
					const recording = !t.end || !isValid(new Date(t.end));
					const end = recording ? roundToNearestMinutes(new Date(), { nearestTo: 1 }) : new Date(t.end);
					return {
						...t,
						start,
						end,
						recording,
						duration: (end.getTime() - start.getTime()) / 1000,
					} as ReportItem<ComegoTime>;
				})
			)
			.then((times) => times?.filter(({ start }) => isWithinInterval(new Date(start), { start: from, end: to })));
	}
	getProjectTimesFiltered(
		from: Date,
		to: Date,
		options: Partial<{
			users: string[];
			projects: string[];
			clients: string[];
			tasks: string[];
			tags: string[];
			billable: ItemBillingFlag;
			completed: boolean;
		}>
	) {
		options = Object.assign(
			{},
			{
				completed: false,
				billable: ItemBillingFlag.BOTH,
			},
			options ?? {}
		);
		const billableMap = {
			[ItemBillingFlag.BILLED]: "billable",
			[ItemBillingFlag.NOT_BILLED]: "not_billable",
			[ItemBillingFlag.BOTH]: null as any,
		};
		return this.timesService.adapter
			.post(environment.serverUrl + "/reports", {
				users: options.users?.length ? options.users : "all",
				projects: options.projects?.length ? options.projects : undefined,
				clients: options.clients?.length ? options.clients : undefined,
				tasks: options.tasks?.length ? options.tasks : undefined,
				tags: options.tags?.length ? options.tags : undefined,
				billable: billableMap[options.billable] ?? null,
				completed: options.completed,
				range: {
					start: subHours(from, 12).toISOString(),
					end: addHours(to, 12).toISOString(),
				},
				type: "projecthours",
			})
			.toPromise<{ data: ReportItem<Time>[] }>()
			.then((times) =>
				times?.data?.map(
					(t) =>
						({
							...t,
							amount: t.amount ?? 0,
							cost: t.cost ?? 0,
							start: new Date(t.start),
							end: t.end ? new Date(t.end) : null,
						}) as any as ReportItem<Time>
				)
			)
			.then((times) => times?.filter(({ start }) => isWithinInterval(new Date(start), { start: from, end: to })));
	}

	teamFilterAllSelected() {
		return this.teamFilter.value.length === this.userSettingsQuery.getValue().workspace.users.length;
	}
	initReportsView() {
		if (this.pipelinesStarted) {
			this.reload();
			return;
		}
		this.pipelinesStarted = true;
		// ****************** Working hours ******************
		combineLatest([
			this.currentPage.asObservable(),
			this.teamFilter.asObservable(),
			this.groupFilter.asObservable(),
			this.comegoTypeFilter.asObservable(),
			this.range$,
			this.userSettingsQuery.select((x) => x.workspace?.settings),
			this.reloadTrigger$,
		])
			.pipe(
				untilDestroyed(this),
				filter(([page]) => page === "workinghours"),
				debounceTimeAfterFirst(100),
				map(([, team, groups, comegoType, range, , reloadTrigger]) => {
					return { team, groups, comegoType, range, reloadTrigger };
				}),
				distinctUntilChangedJson(),
				switchMap(async ({ team, groups, comegoType, range, reloadTrigger }) => {
					const fetchGen = ++this.workinghoursFetchGen;
					const showLoading = !reloadTrigger.silent;
					if (showLoading) this.timeLoading = true;
					const user = this.userSettingsQuery.getValue();
					const _team = this.resolveTeamIds(team, groups);

					const itemType = [...comegoType];
					if (itemType.includes("work")) {
						itemType.push("pause");
					}
					// if allHours + legacy date
					if (user.workspace.settings["allHoursEnabled"] && user.workspace.settings["legacyTimesBefore"]) {
						const allUsersSelected = this.teamFilterAllSelected();
						const legacyDate = new Date(user.workspace.settings["legacyTimesBefore"]);
						// get projecthours and Workinhours , merge as workinghours
						const getWorkingHours = (from: Date, to: Date) =>
							this.getComegoTimesFiltered(
								from,
								to,
								allUsersSelected ? "all" : _team.length ? _team : "all",
								{
									itemType,
									rulebreaks: false,
								}
							);
						const getProjectHours = (from: Date, to: Date) =>
							this.getProjectTimesFiltered(from, to, {
								users: _team,
								projects: [],
								clients: [],
								tasks: [],
								tags: [],
								billable: ItemBillingFlag.BOTH,
								completed: true,
							});
						let data: ReportItem<ComegoTime>[] = [];
						let projecthoursData: ReportItem<ComegoTime>[] = [];
						let workinghoursData: ReportItem<ComegoTime>[] = [];

						if (range.from <= legacyDate) {
							const projectTo = range.to < legacyDate ? range.to : addDays(legacyDate, -1);
							const rawProjectHours = await getProjectHours(range.from, projectTo);
							projecthoursData = rawProjectHours.map(
								(projectHour) =>
									({
										...projectHour,
										entity: "workinghours",
										type: { id: null, name: "work" },
										violations: [],
									}) as ReportItem<ComegoTime>
							);
						}

						if (range.to >= legacyDate) {
							workinghoursData = await getWorkingHours(
								range.from < legacyDate ? legacyDate : range.from,
								range.to
							);
						}

						data = [...projecthoursData, ...workinghoursData];
						const workingHourRules = user.workspace.schedules?.workspace?.workinghours;
						if (projecthoursData.length && workingHourRules) {
							data = attachLegacyProjectHourViolations(
								data,
								new Set(projecthoursData.map((p) => p.id)),
								workingHourRules
							);
						}
						return { data, showLoading, fetchGen };
					} else if (user.workspace.settings["workinghoursEnabled"]) {
						const allUsersSelected = this.teamFilterAllSelected();
						const getWorkingHours = (from: Date, to: Date) =>
							this.getComegoTimesFiltered(
								from,
								to,
								allUsersSelected ? "all" : _team.length ? _team : "all",
								{
									itemType,
									rulebreaks: false,
								}
							);
						return { data: await getWorkingHours(range.from, range.to), showLoading, fetchGen };
					} else {
						const getProjectHours = (from: Date, to: Date) =>
							this.getProjectTimesFiltered(from, to, {
								users: _team,
								projects: [],
								clients: [],
								tasks: [],
								tags: [],
								billable: ItemBillingFlag.BOTH,
								completed: true,
							});

						return { data: await getProjectHours(range.from, range.to), showLoading, fetchGen };
					}
				})
			)
			.subscribe(({ data, showLoading, fetchGen }) => {
				if (fetchGen !== this.workinghoursFetchGen) return;
				if (data) this.workinghours.update(data);
				if (showLoading) this.timeLoading = false;
			});
		// ****************** Project hours ******************
		combineLatest([
			this.currentPage.asObservable(),
			this.teamFilter.asObservable(),
			this.groupFilter.asObservable(),
			this.projectFilter.asObservable(),
			this.clientFilter.asObservable(),
			this.taskFilter.asObservable(),
			this.tagFilter.asObservable().pipe(map((tags) => tags.map((t) => t.id))),
			this.billableFilter.asObservable(),
			this.completedFilter.asObservable(),
			this.range$,
			this.reloadTrigger$,
		])
			.pipe(
				filter(([page]) => page?.split(".")[0] === "project"),
				untilDestroyed(this),
				debounceTimeAfterFirst(100),
				map(([, team, groups, projects, clients, tasks, tags, billable, completed, range, reloadTrigger]) => {
					return {
						team,
						groups,
						projects,
						clients,
						tasks,
						tags,
						billable,
						completed,
						range,
						reloadTrigger,
					};
				}),
				distinctUntilChangedJson(),
				switchMap(
					async ({
						team,
						groups,
						projects,
						clients,
						tasks,
						tags,
						billable,
						completed,
						range,
						reloadTrigger,
					}) => {
						const fetchGen = ++this.projecthoursFetchGen;
						const showLoading = !reloadTrigger.silent;
						if (showLoading) this.timeLoading = true;
						const _team = this.resolveTeamIds(team, groups);
						const getProjectHours = (from: Date, to: Date) =>
							this.getProjectTimesFiltered(from, to, {
								users: this.teamFilterAllSelected() ? [] : _team,
								projects,
								clients,
								tasks,
								tags,
								billable,
								completed: completed !== ItemCompletedFlag.NOT_COMPLETED,
							});
						const data = await getProjectHours(range.from, range.to);
						return { data, showLoading, fetchGen };
					}
				)
			)
			.subscribe(({ data, showLoading, fetchGen }) => {
				if (fetchGen !== this.projecthoursFetchGen) return;
				if (data) this.projecthours.update(data);
				if (showLoading) this.timeLoading = false;
			});
	}
	clearReportData() {
		this.workinghoursFetchGen++;
		this.projecthoursFetchGen++;
		this.workinghours.next([]);
		this.projecthours.next([]);
	}
	ngOnDestroy() {
		this.clearReportData();
		this.data$filtered.unsubscribe();
		this.usersInDataset.unsubscribe();
		this.legacyDate$.unsubscribe();
	}

	updateProjectItem(time: Time) {
		this.projecthours.update((s) => {
			const idx = s.findIndex((x) => x.id === time.id);
			if (idx !== -1) {
				s.splice(idx, 1, ...(this.transformTimeToReportItem(time) as ReportItem<Time>[]));
			} else s.push(...(this.transformTimeToReportItem(time) as ReportItem<Time>[]));
			return s;
		});
	}
	updateWorkingItem(time: ComegoTime) {
		this.workinghours.update((s) => {
			const idx = s.findIndex((x) => x.id === time.id);
			if (idx !== -1) {
				s.splice(idx, 1, ...(this.transformTimeToReportItem(time) as ReportItem<ComegoTime>[]));
			} else s.push(...(this.transformTimeToReportItem(time) as ReportItem<ComegoTime>[]));
			return s;
		});
	}
	private readonly transformTimeToReportItem = useTransformTimeToReportItem();
	async getDashboardFromUser() {
		return await this.timesService.adapter
			.get<SavedDashboard[]>(
				environment.serverUrl +
					`/get/saved_dashboards?${new URLSearchParams({
						$filter: `editor eq '${this.userSettingsQuery.getValue().id}' `,
					}).toString()}`
			)
			.toPromise()
			.then((data) => {
				this.dashboards.update(data);
			});
	}
	async updateDashboardColums() {
		// update Dashboard
		if (this.activeFavDashboard.value) {
			let dashboard = this.activeFavDashboard.value;
			const appSettings = this.appSettings.getValue() as any;
			dashboard = {
				...dashboard,
				columns:
					dashboard.tabType == 0
						? (appSettings.reports?.export.columns as string[])
						: (appSettings.schedule?.export.columns as string[]),
			};
			await toPromise(
				this.timesService.adapter.post<SavedDashboard>(
					environment.serverUrl + "/update/saved_dashboards/" + dashboard.id,
					dashboard
				)
			).then((x) => {
				//  reload saved dashboard data
				this.activeFavDashboard.next(x);
				this.dashboards.update((oldValues) => {
					let newValues = [...oldValues];
					const index = newValues.findIndex((d) => d.id == x.id);
					if (index == -1) return newValues;
					newValues[index] = {
						...newValues[index],
						...x,
					};
					return newValues;
				});
			});
		}
	}

	async loadTasks() {
		await toPromise(this.timesService.adapter.get<Task[]>(environment.serverUrl + "/get/tasks")).then((x) => {
			this.tasks.update(x);
		});
	}
}
